/**
 * Leaderboard Service
 * Handles all leaderboard-related API calls
 */

import { ApiClient } from '../core/api-client';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';

export interface LeaderboardEntry {
  rank: number;
  name: string;
  amount?: number;
  points?: number;
  [key: string]: unknown;
}

export interface LeaderboardData {
  entries?: LeaderboardEntry[];
  [key: string]: unknown;
}

class LeaderboardService {
  private readonly client: ApiClient;

  constructor(client: ApiClient) {
    this.client = client;
  }

  /**
   * Get user monthly leaderboard
   */
  async getUserMonthlyLeaderboard(signal?: AbortSignal): Promise<LeaderboardData | null> {
    try {
      const response = await this.client.get<LeaderboardData>(
        '/devotee/leaderboard/user/monthly',
        { signal },
      );
      return response.data || null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Get user quarterly leaderboard
   */
  async getUserQuarterlyLeaderboard(signal?: AbortSignal): Promise<LeaderboardData | null> {
    try {
      const response = await this.client.get<LeaderboardData>(
        '/devotee/leaderboard/user/quarterly',
        { signal },
      );
      return response.data || null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Get user yearly leaderboard
   */
  async getUserYearlyLeaderboard(signal?: AbortSignal): Promise<LeaderboardData | null> {
    try {
      const response = await this.client.get<LeaderboardData>(
        '/devotee/leaderboard/user/yearly',
        { signal },
      );
      return response.data || null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Get event monthly leaderboard
   */
  async getEventMonthlyLeaderboard(
    eventId: number,
    signal?: AbortSignal,
  ): Promise<LeaderboardData | null> {
    try {
      const response = await this.client.get<LeaderboardData>(
        `/devotee/leaderboard/event/monthly/${eventId}`,
        { signal },
      );
      return response.data || null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Get event quarterly leaderboard
   */
  async getEventQuarterlyLeaderboard(
    eventId: number,
    signal?: AbortSignal,
  ): Promise<LeaderboardData | null> {
    try {
      const response = await this.client.get<LeaderboardData>(
        `/devotee/leaderboard/event/quarterly/${eventId}`,
        { signal },
      );
      return response.data || null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Get event yearly leaderboard
   */
  async getEventYearlyLeaderboard(
    eventId: number,
    signal?: AbortSignal,
  ): Promise<LeaderboardData | null> {
    try {
      const response = await this.client.get<LeaderboardData>(
        `/devotee/leaderboard/event/yearly/${eventId}`,
        { signal },
      );
      return response.data || null;
    } catch (error) {
      return null;
    }
  }
}

export const leaderboardService = new LeaderboardService(ApiClient.getInstance(API_BASE_URL));
